import { useMemo, useState, useEffect, useRef } from 'react';
import { useOutletContext, useParams, useNavigate } from 'react-router-dom';
import { Button, Modal, Form, Input, InputNumber, Select, message } from 'antd';
import Calendar from '../../calendar';
import CommonTable from '../../commonTable';
import { sessionColumns } from './columns';
import {
  TeamOutlined, ThunderboltOutlined, DollarOutlined, TrophyOutlined, PlusOutlined, DeleteOutlined, CalendarOutlined,
  LeftOutlined, RightOutlined, DownOutlined, CalculatorOutlined, CheckCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined,
  FilePdfOutlined, FileExcelOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useGetTrainerIncentivesQuery, useAddTrainerIncentiveMutation, useToggleTrainerIncentiveStatusMutation, useGetTrainerIncentiveSummaryQuery, useGetTrainerClientsQuery, useGetTrainerIncentiveCalculateQuery, useGenerateTrainerIncentiveMutation } from '../../../services/trainer';
import { formatDate } from '../../../utils/date';
import { exportIncentiveSessionsToExcel } from '../../../utils/exportIncentiveSessions';
import usePermissions from '../../../hooks/usePermissions';
import './styles.scss';

// Permission keys gating the incentive actions below — none of these are wired
// up on the backend's role/permission schema yet, so only SUPERADMIN (which
// bypasses all permission checks) will see them until the backend adds these
// keys/permissions and assigns them to the relevant roles.
// Backend key (permission string: TRAINER_INCENTIVE_GENERATE).
const GENERATE_INCENTIVE_PERM_KEY     = 'trainer-ud-incentive-generate';
// Role screen has "Add Slab" and "Slab Toggle" (no separate "Slab View"). Keys follow the
// backend's `trainer-ud-incentive-*` pattern; the permission strings are checked too.
const ADD_INCENTIVE_SLAB_PERMS    = ['trainer-ud-incentive-slab-add', 'TRAINER_INCENTIVE_SLAB_ADD', '8-3-td-incentive-slab-add'];
const TOGGLE_INCENTIVE_SLAB_PERMS = ['trainer-ud-incentive-slab-toggle', 'TRAINER_INCENTIVE_SLAB_TOGGLE'];

/* ─── Helpers ─────────────────────────────────────────────────────────── */

const inr = (n: number) => `₹${n.toLocaleString('en-IN')}`;

// Calendar quick-range shortcuts for the month/period filter.
const RANGE_PRESETS = [
  { label: 'Today',        getRange: () => [dayjs().startOf('day').toDate(), dayjs().endOf('day').toDate()] as [Date, Date] },
  { label: 'Yesterday',    getRange: () => [dayjs().subtract(1, 'day').startOf('day').toDate(), dayjs().subtract(1, 'day').endOf('day').toDate()] as [Date, Date] },
  { label: 'This Week',    getRange: () => [dayjs().startOf('week').toDate(), dayjs().endOf('day').toDate()] as [Date, Date] },
  { label: 'Last 15 Days', getRange: () => [dayjs().subtract(14, 'day').startOf('day').toDate(), dayjs().endOf('day').toDate()] as [Date, Date] },
  { label: 'This Month',   getRange: () => [dayjs().startOf('month').toDate(), dayjs().endOf('month').toDate()] as [Date, Date] },
  { label: 'Last Month',   getRange: () => [dayjs().subtract(1, 'month').startOf('month').toDate(), dayjs().subtract(1, 'month').endOf('month').toDate()] as [Date, Date] },
  { label: 'All Time',     getRange: () => [dayjs('2000-01-01').toDate(), dayjs().endOf('day').toDate()] as [Date, Date] },
];

/* ─── Component ───────────────────────────────────────────────────────── */

interface Slab { from: number; to: number; percentage: number; }
interface AddIncentiveValues { slabs: Slab[]; }

interface SlabEntry { id: string; label: string; time: string; status: string; createdBy: string; createdById: string; slabs: Slab[]; }

const IncentiveCalculation = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { trainer } = useOutletContext<{ trainer: any }>() ?? { trainer: {} };
  const trainerId: string = (id as string) || trainer?._id;
  const { hasPermission } = usePermissions();
  const canAddSlab    = ADD_INCENTIVE_SLAB_PERMS.some(k => hasPermission(k));
  const canToggleSlab = TOGGLE_INCENTIVE_SLAB_PERMS.some(k => hasPermission(k));
  // "Incentive Slab" opens the slab list — show it to anyone who can add or toggle slabs.
  const canViewSlabs  = canAddSlab || canToggleSlab;

  const [range, setRange] = useState<{ start: Dayjs; end: Dayjs }>({
    start: dayjs().startOf('month'),
    end: dayjs().endOf('month'),
  });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [calOpen, setCalOpen] = useState(false);
  const [datePicked, setDatePicked] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [tdsPercent, setTdsPercent] = useState<number>(10);
  const [gstPercent, setGstPercent] = useState<number>(5);
  const [rewards, setRewards] = useState<{ name: string; value: number; remark?: string }[]>([]);
  const [rewardModalOpen, setRewardModalOpen] = useState(false);
  const [deduction, setDeduction] = useState<number>(0);
  const [deductionRemark, setDeductionRemark] = useState('');
  const [clientId, setClientId] = useState<string | undefined>(undefined);
  const calRef = useRef<HTMLDivElement>(null);
  const [form] = Form.useForm<AddIncentiveValues>();
  const [rewardForm] = Form.useForm<{ name: string; value: number; remark?: string }>();
  const PAGE_SIZE = 6;

  // Close the month calendar popup on outside click.
  useEffect(() => {
    if (!calOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (calRef.current && !calRef.current.contains(e.target as Node)) setCalOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [calOpen]);

  // GET all incentive records (latest first, each with slabs + status).
  const { data: incResp, isFetching: incLoading } = useGetTrainerIncentivesQuery(
    { trainerId },
    { skip: !trainerId },
  );
  const [addTrainerIncentive, { isLoading: adding }] = useAddTrainerIncentiveMutation();
  const [toggleStatus, { isLoading: toggling }] = useToggleTrainerIncentiveStatusMutation();

  // GET a live preview of the payout (sessions/revenue/slab % + TDS/GST breakup) while the modal is open.
  const { data: calcResp, isFetching: calculating } = useGetTrainerIncentiveCalculateQuery(
    {
      trainerId,
      fromDate: range.start.format('YYYY-MM-DD'),
      toDate: range.end.format('YYYY-MM-DD'),
      tdsPercent,
      gstPercent,
    },
    { skip: !trainerId || !generateOpen },
  );
  const [generateIncentive, { isLoading: generating }] = useGenerateTrainerIncentiveMutation();

  // GET the incentive summary (KPIs + breakdown + payout) for the selected month.
  const { data: summaryResp, isFetching: sessLoading } = useGetTrainerIncentiveSummaryQuery(
    { trainerId, fromDate: range.start.format('YYYY-MM-DD'), toDate: range.end.format('YYYY-MM-DD'), clientId },
    { skip: !trainerId },
  );

  // GET the trainer's clients for the selected period, to filter the summary by one of them.
  const { data: clientsResp } = useGetTrainerClientsQuery(
    { trainerId, fromDate: range.start.format('YYYY-MM-DD'), toDate: range.end.format('YYYY-MM-DD') },
    { skip: !trainerId },
  );
  const clientOptions = useMemo(() => {
    const r: any = clientsResp;
    const list: any[] = Array.isArray(r) ? r
      : Array.isArray(r?.data) ? r.data
      : Array.isArray(r?.clients) ? r.clients
      : Array.isArray(r?.data?.clients) ? r.data.clients
      : [];
    return list.map((c: any) => ({ value: c.clientId ?? c.userId ?? c._id ?? c.id, label: c.name ?? c.clientName ?? c.userName ?? '—' }));
  }, [clientsResp]);

  // Map the API response into history entries (shape kept flexible).
  const history: SlabEntry[] = useMemo(() => {
    const raw = (incResp as any)?.data ?? incResp;
    if (!raw) return [];
    const list = Array.isArray(raw) ? raw : [raw];
    return list
      .filter((x: any) => x && Array.isArray(x.slabs) && x.slabs.length)
      .map((x: any, i: number) => ({
        id:          x._id ?? x.id ?? `inc-${i}`,
        label:       x.createdAt ? formatDate(x.createdAt) : `Slab set ${i + 1}`,
        time:        x.createdAt ? dayjs(x.createdAt).format('hh:mm A') : '',
        status:      x.status ?? 'inactive',
        createdBy:   x.createdBy?.name ?? x.createdBy ?? '',
        createdById: x.createdBy?._id ?? x.createdBy?.id ?? '',
        slabs:       x.slabs.map((s: any) => ({ from: s.from, to: s.to, percentage: s.percentage })),
      }));
  }, [incResp]);

  const handleAddIncentive = async (values: AddIncentiveValues) => {
    if (!trainerId) { message.error('Trainer not found'); return; }
    try {
      await addTrainerIncentive({
        trainerId,
        slabs: (values.slabs || []).map(s => ({ from: s.from, to: s.to, percentage: s.percentage })),
      }).unwrap();
      setAddOpen(false);
      form.resetFields();
    } catch {
      /* error (incl. 409 active-exists) surfaced by the global bad-request handler */
    }
  };

  const handleToggleStatus = async (entry: SlabEntry) => {
    try {
      await toggleStatus({ trainerId, incentiveId: entry.id }).unwrap();
    } catch {
      /* 409 when activating while another is active — surfaced globally */
    }
  };

  const handleGenerateIncentive = async () => {
    if (!trainerId) { message.error('Trainer not found'); return; }
    try {
      await generateIncentive({
        trainerId,
        fromDate: range.start.format('YYYY-MM-DD'),
        toDate: range.end.format('YYYY-MM-DD'),
        tdsPercent,
        gstPercent,
        additionalFields: rewards.map(r => ({ fieldName: r.name, amount: r.value, remark: r.remark })),
        ...(deduction ? { deduction, deductionRemark } : {}),
      }).unwrap();
      setGenerateOpen(false);
    } catch {
      /* error surfaced by the global bad-request handler */
    }
  };

  const handleAddReward = (values: { name: string; value: number; remark?: string }) => {
    setRewards(r => [...r, { name: values.name, value: Number(values.value) || 0, remark: values.remark }]);
    rewardForm.resetFields();
    setRewardModalOpen(false);
  };

  const handleRemoveReward = (idx: number) => {
    setRewards(r => r.filter((_, i) => i !== idx));
  };

  const exportMeta = {
    trainerName: trainer?.name,
    fromDate:    range.start.format('YYYY-MM-DD'),
    toDate:      range.end.format('YYYY-MM-DD'),
  };
  const handleExportExcel = () => exportIncentiveSessionsToExcel(sessions, exportMeta);

  // Prints the page exactly as it looks on screen (same cards + table design)
  // via the browser's print dialog. Rather than cloning the live DOM into a
  // separate popup document (fragile — needs every stylesheet rule copied
  // over and re-applied in a foreign document context), this prints the
  // current page itself: a scoped print stylesheet hides everything except
  // `.ic-print-area`, so the already-rendered, already-correctly-styled
  // elements are what actually reach the printer/PDF.
  const handleExportPDF = () => window.print();

  // ── Derive KPIs + per-client sessions from the summary API. ──
  const summary: any = (summaryResp as any)?.data ?? summaryResp ?? {};

  // ── Table data comes from the summary API's `sessions` array. ──
  const sessions: any[] = Array.isArray(summary.sessions) ? summary.sessions : [];

  const totalSessions  = Number(summary.totalSessions ?? 0);
  const activeClients  = Number(summary.activeClients ?? summary.clientsTrained ?? new Set(sessions.map(s => s.clientId ?? s.name)).size);
  const totalClients   = Number(summary.totalClients ?? activeClients);
  const revenue        = Number(summary.revenueGenerated ?? summary.revenue ?? 0);
  const sessionRevenue   = Number(summary?.sessionRevenue) || 0;

  const kpis = [
    { key: 'sessions', label: 'Total Sessions',    value: `${totalSessions}`,   icon: <ThunderboltOutlined />, color: '#6366f1' },
    { key: 'totalClients', label: 'Total Clients', value: `${totalClients}`,    icon: <TeamOutlined />,        color: '#0ea5e9' },
    { key: 'revenue',  label: 'Revenue Generated', value: inr(revenue),         icon: <DollarOutlined />,      color: '#f59e0b' },
    { key: 'avg',      label: 'Session Revenue',      value: `${sessionRevenue}`,    icon: <TrophyOutlined />,      color: '#ec4899' },
  ];

  // ── Incentive slab % applicable to the revenue generated in the selected range. ──
  const activeSlabSet = history.find(h => h.status === 'active');
  const matchedSlab = activeSlabSet?.slabs.find(s => revenue >= s.from && revenue <= s.to);
  const slabPercentage = matchedSlab?.percentage ?? 0;
  const incentiveAmount = Math.round((revenue * slabPercentage) / 100);

  // ── Live preview from the incentive-calculate API. ──
  const calc: any = (calcResp as any)?.data ?? calcResp ?? null;
  const dispSessions        = Number(calc?.noOfSessions ?? totalSessions);
  const dispRevenue         = Number(calc?.revenueGenerated ?? revenue);
  const dispSessionRevenue  = Number(calc?.sessionRevenue ?? sessionRevenue);
  const dispSlabPercentage  = Number(calc?.slabPercentage ?? slabPercentage);
  const dispIncentiveAmount = Number(calc?.incentiveAmount ?? incentiveAmount);
  const tdsAmount  = Number(calc?.tdsAmount ?? 0);
  const gstAmount  = Number(calc?.gstAmount ?? 0);
  const netAmount  = Number(calc?.totalAmount ?? (dispIncentiveAmount - tdsAmount - gstAmount));
  const totalRewards = rewards.reduce((sum, r) => sum + (Number(r.value) || 0), 0);
  const grandTotal = netAmount + totalRewards - deduction;
  const hasActiveSlab   = calc ? Boolean(calc.hasActiveSlab) : true;
  const slabMatched     = calc ? Boolean(calc.slabMatched) : true;
  const alreadyGenerated = Boolean(calc?.alreadyGenerated);
  const canGenerate = hasActiveSlab && slabMatched && !alreadyGenerated;
  const noticeType: 'success' | 'warning' | 'info' = alreadyGenerated
    ? 'info'
    : (!hasActiveSlab || !slabMatched) ? 'warning' : 'success';
  const noticeIcon = noticeType === 'info'
    ? <InfoCircleOutlined />
    : noticeType === 'warning' ? <ExclamationCircleOutlined /> : <CheckCircleOutlined />;

  return (
    <div className="incentive-calculation ic-print-area">
      {/* ── Header ── */}
      <div className="ic-head">
        <div className="ic-head-top">
          <div>
            <h3 className="ic-title">Incentive Calculation</h3>
            <p className="ic-sub">Performance-based incentive for the selected period</p>
          </div>
          <div className="ic-active-clients">
            <svg className="ic-active-clients-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.8" />
              <path d="M5 19c0-3.3137 3.134-6 7-6s7 2.6863 7 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            <span className="ic-active-clients-value">{activeClients}</span>
            <span className="ic-active-clients-text">Active Client{activeClients === 1 ? '' : 's'}</span>
          </div>
        </div>
        <div className="ic-head-actions">
          <div className="ic-cal-wrap" ref={calRef}>
            <button className="ic-cal-trigger" onClick={() => setCalOpen(o => !o)}>
              <CalendarOutlined /> <strong>{range.start.format('DD MMM')} – {range.end.format('DD MMM YYYY')}</strong>
              <DownOutlined className="ic-cal-caret" />
            </button>
            {calOpen && (
              <div className="ic-cal-popup" onClick={e => e.stopPropagation()}>
                <Calendar
                  rangePresets={RANGE_PRESETS}
                  selectedDate={{ start: range.start.toDate(), end: range.end.toDate() }}
                  onDateChange={(v: any) => {
                    if (v?.start && v?.end) {
                      setRange({ start: dayjs(v.start), end: dayjs(v.end) });
                      setDatePicked(true);
                      setCalOpen(false);
                    }
                  }}
                />
              </div>
            )}
          </div>
          {datePicked && hasPermission(GENERATE_INCENTIVE_PERM_KEY) && (
            <Button
              type="primary"
              className="ic-generate-btn"
              icon={<CalculatorOutlined />}
              onClick={() => {
                setDeduction(0);
                setDeductionRemark('');
                setGenerateOpen(true);
              }}
            >
              Generate Incentive
            </Button>
          )}
          {canViewSlabs && (
            <Button type="primary" className="ic-add-btn" onClick={() => setHistoryOpen(true)}>
              Incentive Slab
            </Button>
          )}
          <Button
            className="ic-export-btn"
            icon={<FileExcelOutlined />}
            disabled={sessions.length === 0}
            onClick={handleExportExcel}
          >
            Excel
          </Button>
          <Button
            className="ic-export-btn"
            icon={<FilePdfOutlined />}
            disabled={sessions.length === 0}
            onClick={handleExportPDF}
          >
            PDF
          </Button>
          <Select
            allowClear
            showSearch
            placeholder="All Clients"
            className="ic-client-select"
            style={{ minWidth: 180, height: 41 }}
            value={clientId}
            onChange={(v) => setClientId(v)}
            options={clientOptions}
            optionFilterProp="label"
          />
        </div>
      </div>

      {/* ── KPI cards ── */}
      <div className="ic-kpis">
        {kpis.map(k => (
          <div className="ic-kpi" key={k.key}>
            <span className="ic-kpi-icon" style={{ background: `${k.color}22`, color: k.color }}>{k.icon}</span>
            <div>
              <div className="ic-kpi-label">{k.label}</div>
              <div className="ic-kpi-value">{k.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Sessions ── */}
      <CommonTable
        columns={sessionColumns}
        dataSource={sessions}
        loading={sessLoading}
        rowKey={(r: any) => r.sessionId}
        pagination={false}
        scroll={{ x: 900 }}
        className="ic-sess-table-antd"
      />

      {/* ── Generate Incentive modal ── */}
      <Modal
        title="Generate Incentive"
        open={generateOpen}
        onCancel={() => setGenerateOpen(false)}
        width={680}
        className="ic-generate-modal"
        footer={[
          <Button key="cancel" onClick={() => setGenerateOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="generate"
            type="primary"
            className="ic-generate-btn"
            loading={generating}
            disabled={!canGenerate}
            onClick={handleGenerateIncentive}
          >
            Generate
          </Button>,
        ]}
      >
        <div className="ic-gen">
          <div className="ic-gen-period">
            <CalendarOutlined />
            <span>{range.start.format('DD MMM YYYY')} – {range.end.format('DD MMM YYYY')}</span>
          </div>

          {calc?.notification && (
            <div className={`ic-gen-notice ic-gen-notice-${noticeType}`}>
              {noticeIcon}
              <span>{calc.notification}</span>
            </div>
          )}

          <div className="ic-gen-inputs">
            <div className="ic-gen-input">
              <label>TDS %</label>
              <InputNumber
                min={0}
                max={100}
                value={tdsPercent}
                parser={(value) => (value ? value.replace(/[^\d]/g, '') : '') as unknown as number}
                onChange={(v) => setTdsPercent(Number(v) || 0)}
              />
            </div>
            <div className="ic-gen-input">
              <label>GST %</label>
              <InputNumber
                min={0}
                max={100}
                value={gstPercent}
                parser={(value) => (value ? value.replace(/[^\d]/g, '') : '') as unknown as number}
                onChange={(v) => setGstPercent(Number(v) || 0)}
              />
            </div>
            <div className="ic-gen-input">
              <label>Deduction (optional)</label>
              <InputNumber
                min={0}
                value={deduction}
                placeholder="e.g. advance already paid"
                onChange={(v) => setDeduction(Number(v) || 0)}
              />
            </div>
          </div>

          {deduction > 0 && (
            <div className="ic-gen-input" style={{ marginBottom: 16 }}>
              <label>Deduction Remark</label>
              <Input.TextArea
                rows={2}
                placeholder="e.g. Advance already diya tha, wahi adjust kiya"
                value={deductionRemark}
                onChange={(e) => setDeductionRemark(e.target.value)}
              />
            </div>
          )}

          <div className="ic-gen-cards">
            <div className="ic-gen-card">
              <span className="ic-gen-card-icon" style={{ background: '#6366f122', color: '#6366f1' }}><ThunderboltOutlined /></span>
              <div>
                <div className="ic-gen-card-label">No. of Sessions</div>
                <div className="ic-gen-card-value">{dispSessions}</div>
              </div>
            </div>
            <div className="ic-gen-card">
              <span className="ic-gen-card-icon" style={{ background: '#f59e0b22', color: '#f59e0b' }}><DollarOutlined /></span>
              <div>
                <div className="ic-gen-card-label">Revenue Generated</div>
                <div className="ic-gen-card-value">{inr(dispRevenue)}</div>
              </div>
            </div>
            <div className="ic-gen-card">
              <span className="ic-gen-card-icon" style={{ background: '#ec489922', color: '#ec4899' }}><TrophyOutlined /></span>
              <div>
                <div className="ic-gen-card-label">Session Revenue</div>
                <div className="ic-gen-card-value">{inr(dispSessionRevenue)}</div>
              </div>
            </div>
            <div className="ic-gen-card">
              <span className="ic-gen-card-icon" style={{ background: '#10b98122', color: '#10b981' }}><CalculatorOutlined /></span>
              <div>
                <div className="ic-gen-card-label">Incentive Slab %</div>
                <div className="ic-gen-card-value">{dispSlabPercentage}%</div>
              </div>
            </div>
          </div>

          {calc ? (
            <div className="ic-gen-result">
              <div className="ic-gen-result-row">
                <span>Session Revenue</span>
                <span className="ic-amount">{inr(dispSessionRevenue)}</span>
              </div>
              <div className="ic-gen-result-row">
                <span>GST ({gstPercent}%)</span>
                <span className="ic-amount ic-neg">− {inr(gstAmount)}</span>
              </div>
              <div className="ic-gen-result-row">
                <span>Incentive amount ({dispSlabPercentage}%)</span>
                <span className="ic-amount">{inr(dispIncentiveAmount)}</span>
              </div>
              <div className="ic-gen-result-row">
                <span>TDS ({tdsPercent}%)</span>
                <span className="ic-amount ic-neg">− {inr(tdsAmount)}</span>
              </div>
              <div className="ic-gen-total">
                <span>Total Payable</span>
                <span className="ic-gen-total-value">{inr(netAmount)}</span>
              </div>

              <div className="ic-gen-rewards">
                <div className="ic-gen-rewards-head">
                  <span>Rewards</span>
                  <Button size="small" icon={<PlusOutlined />} onClick={() => setRewardModalOpen(true)}>
                    Add
                  </Button>
                </div>
                {rewards.length > 0 && (
                  <div className="ic-gen-reward-list">
                    {rewards.map((r, idx) => (
                      <div className="ic-gen-result-row ic-gen-reward-item" key={idx}>
                        <span className="ic-gen-reward-name">
                          {r.name}
                          {r.remark && <span className="ic-gen-reward-remark">{r.remark}</span>}
                        </span>
                        <span>
                          <span className="ic-amount">{inr(r.value)}</span>
                          <DeleteOutlined className="ic-gen-reward-del" onClick={() => handleRemoveReward(idx)} />
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {deduction > 0 && (
                <div className="ic-gen-result-row">
                  <span>Deduction{deductionRemark ? ` (${deductionRemark})` : ''}</span>
                  <span className="ic-amount ic-neg">− {inr(deduction)}</span>
                </div>
              )}

              <div className="ic-gen-total">
                <span>Net Payable</span>
                <span className="ic-gen-total-value">{inr(grandTotal)}</span>
              </div>
            </div>
          ) : (
            <div className="ic-gen-total">
              <span>Incentive Amount</span>
              <span className="ic-gen-total-value">{inr(dispIncentiveAmount)}</span>
            </div>
          )}
        </div>
      </Modal>

      {/* ── Add Reward modal ── */}
      <Modal
        title="Add Reward"
        open={rewardModalOpen}
        onCancel={() => { setRewardModalOpen(false); rewardForm.resetFields(); }}
        onOk={() => rewardForm.submit()}
        okText="Add"
        destroyOnClose
      >
        <Form form={rewardForm} layout="vertical" onFinish={handleAddReward}>
          <Form.Item name="name" label="Reward Name" rules={[{ required: true, message: 'Enter a reward name' }]}>
            <Input placeholder="e.g. Performance Bonus" />
          </Form.Item>
          <Form.Item name="value" label="Value" rules={[{ required: true, message: 'Enter a value' }]}>
            <InputNumber style={{ width: '100%' }} min={0} placeholder="Amount" />
          </Form.Item>
          <Form.Item name="remark" label="Remark" rules={[{required:true,message:"Enter a remark"}]}>
            <Input.TextArea rows={3} placeholder="Optional note about this reward" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Incentive Slab history modal ── */}
      <Modal
        open={historyOpen}
        onCancel={() => setHistoryOpen(false)}
        footer={null}
        width={860}
        className="ic-history-modal"
        title={
          <div className="ic-history-head">
            <span>Incentive Slabs</span>
            {canAddSlab && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
                Add Incentive
              </Button>
            )}
          </div>
        }
      >
        {incLoading ? (
          <div className="ic-hist-empty">Loading…</div>
        ) : history.length === 0 ? (
          <div className="ic-hist-empty">No incentive slabs added yet.</div>
        ) : (
          <>
            <div className="ic-cards">
              {history.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((entry) => (
                <div className={`ic-card ${entry.status === 'active' ? 'is-active' : ''}`} key={entry.id}>
                  <div className="ic-card-head">
                    <div className="ic-card-meta">
                      <span className="ic-card-month">
                        <CalendarOutlined /> {entry.label}
                        {entry.time && <span className="ic-card-time">{entry.time}</span>}
                      </span>
                      {entry.createdBy && (
                        entry.createdById ? (
                          <span
                            className="ic-card-by ic-card-by-link"
                            onClick={() => navigate(`/user-detail/${entry.createdById}/attendance`)}
                          >
                            {entry.createdBy}
                          </span>
                        ) : (
                          <span className="ic-card-by">{entry.createdBy}</span>
                        )
                      )}
                    </div>
                    <span className={`ic-status ic-status-${entry.status}`}>{entry.status}</span>
                  </div>
                  <div className="ic-card-slabs">
                    {entry.slabs.map((s, j) => (
                      <div className="ic-slab-chip" key={j}>
                        <div className="ic-slab-range">
                          <span className="ic-slab-cap">Range</span>
                          <span className="ic-slab-val">
                            ₹{Number(s.from).toLocaleString('en-IN')} – ₹{Number(s.to).toLocaleString('en-IN')}
                          </span>
                        </div>
                        <span className="ic-slab-pct">{s.percentage}%</span>
                      </div>
                    ))}
                  </div>
                  {canToggleSlab && (
                    <div className="ic-card-foot">
                      <Button
                        size="small"
                        className={`ic-toggle-btn ${entry.status === 'active' ? 'deactivate' : 'activate'}`}
                        loading={toggling}
                        onClick={() => handleToggleStatus(entry)}
                      >
                        {entry.status === 'active' ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {history.length > PAGE_SIZE && (
              <div className="ic-pager">
                <Button
                  size="small"
                  icon={<LeftOutlined />}
                  disabled={page === 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                />
                <span className="ic-pager-info">
                  Page {page} of {Math.ceil(history.length / PAGE_SIZE)}
                </span>
                <Button
                  size="small"
                  icon={<RightOutlined />}
                  disabled={page >= Math.ceil(history.length / PAGE_SIZE)}
                  onClick={() => setPage(p => Math.min(Math.ceil(history.length / PAGE_SIZE), p + 1))}
                />
              </div>
            )}
          </>
        )}
      </Modal>

      {/* ── Add Incentive modal ── */}
      <Modal
        title="Add Incentive"
        open={addOpen}
        onCancel={() => { setAddOpen(false); form.resetFields(); }}
        onOk={() => form.submit()}
        okText="Add"
        confirmLoading={adding}
        className="ic-add-modal"
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddIncentive}
          className="ic-form"
          initialValues={{ slabs: [{}] }}
        >
          <label className="ic-slab-label">Slabs (From – To → %)</label>
          <Form.List name="slabs">
            {(fields, { add, remove }) => (
              <div className="ic-slabs">
                {fields.map((field, idx) => (
                  <div className="ic-slab-row" key={field.key}>
                    <Form.Item name={[field.name, 'from']} rules={[{ required: true, message: 'From' }]}>
                      <InputNumber style={{ width: '100%' }} min={0} placeholder="From" />
                    </Form.Item>
                    <Form.Item name={[field.name, 'to']} rules={[{ required: true, message: 'To' }]}>
                      <InputNumber style={{ width: '100%' }} min={0} placeholder="To" />
                    </Form.Item>
                    <Form.Item name={[field.name, 'percentage']} rules={[{ required: true, message: '%' }]}>
                      <InputNumber style={{ width: '100%' }} min={0} max={100} placeholder="%" />
                    </Form.Item>
                    {idx === fields.length - 1 ? (
                      <Button type="primary" className="ic-slab-add" icon={<PlusOutlined />} onClick={() => add()} />
                    ) : (
                      <Button className="ic-slab-del" icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default IncentiveCalculation;
